import IconRender from 'components/common/IconRender'
import LanguageSwitcher from 'components/language/LanguageSelector'
import { useAuth } from 'pages/authenticated/AuthContext'
import { AppRole, usePermissions } from 'pages/authenticated/PermissionsContext'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CustomColorsRef, IconSizes } from 'types/general'

function looksLikeFirebaseUid(value: string): boolean {
  // Firebase UIDs are long opaque alphanumerics — never show them as a "name".
  return value.length >= 20 && /^[A-Za-z0-9]+$/.test(value)
}

function resolveDisplayName(
  user: { name?: unknown; email?: unknown; id?: unknown; uid?: unknown } | null | undefined
): string {
  const email = typeof user?.email === 'string' ? user.email.trim() : ''
  const name = typeof user?.name === 'string' ? user.name.trim() : ''
  const subject =
    (typeof user?.id === 'string' && user.id) ||
    (typeof user?.uid === 'string' && user.uid) ||
    ''

  if (name && name !== subject && !looksLikeFirebaseUid(name)) {
    return name
  }
  if (email) return email.split('@')[0] || email
  return ''
}

function resolveAvatarUrl(user: { image?: unknown; picture?: unknown } | null | undefined): string {
  const image = typeof user?.image === 'string' ? user.image.trim() : ''
  if (image) return image
  const picture = typeof user?.picture === 'string' ? user.picture.trim() : ''
  return picture
}

function roleLabelKey(role: AppRole | null): string | null {
  if (!role) return null
  return `common.roles.${role}`
}

type MenuItem =
  | {
      kind: 'route'
      path: string
      labelKey: string
      icon: string
    }
  | {
      kind: 'hash'
      hash: string
      labelKey: string
      icon: string
    }

const ACCOUNT_MENU_ITEMS: MenuItem[] = [
  {
    kind: 'route',
    path: '/profile',
    labelKey: 'common.menu.profile',
    icon: 'regular-user',
  },
  {
    kind: 'hash',
    hash: 'my-space-listings',
    labelKey: 'common.menu.myListings',
    icon: 'regular-house',
  },
  {
    kind: 'hash',
    hash: 'my-space-favorites',
    labelKey: 'common.menu.myFavorites',
    icon: 'regular-heart',
  },
  {
    kind: 'hash',
    hash: 'my-space-applications',
    labelKey: 'common.menu.myApplications',
    icon: 'regular-clipboard-list',
  },
]

type UserMenuProps = {
  /** Compact trigger for narrow headers (hides name/role text). */
  compact?: boolean
}

const UserMenu = ({ compact = false }: UserMenuProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout, isAuthenticated } = useAuth()
  const { role, emailValidated, refresh } = usePermissions()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Heal stale Locataire label when the user already owns listings (no create-ad intent).
  useEffect(() => {
    if (!isAuthenticated) return
    void refresh()
  }, [isAuthenticated, refresh])

  const displayName = useMemo(() => {
    const resolved = resolveDisplayName(user)
    return resolved || t('common.mySpace')
  }, [user, t])

  const avatarUrl = resolveAvatarUrl(user)
  const initial = displayName.charAt(0).toUpperCase() || '?'
  const roleKey = roleLabelKey(role)
  const roleLabel = roleKey ? String(t(roleKey)) : ''

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const goToAccountOrVerify = (target: string) => {
    setIsOpen(false)
    void (async () => {
      const payload = await refresh()
      const isOwner =
        payload?.role === 'owner' ||
        role === 'owner' ||
        Boolean(payload?.hasListing)
      const validated = payload?.emailValidated ?? emailValidated
      if (isOwner && !validated) {
        navigate('/verify-email', { state: { next: target } })
        return
      }
      navigate(target)
    })()
  }

  const goToAccountSection = (hash: string) => {
    goToAccountOrVerify(`/account#${hash}`)
  }

  const handleMenuItem = (item: MenuItem) => {
    if (item.kind === 'route') {
      goToAccountOrVerify(item.path)
      return
    }
    goToAccountOrVerify(`/account#${item.hash}`)
  }

  const handleLogout = () => {
    setIsOpen(false)
    void logout()
  }

  return (
    <div className="flex items-center gap-2 sm:gap-3" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => goToAccountSection('my-space-alerts')}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-imovSecondary transition-colors hover:bg-gray-50"
        aria-label={t('common.notifications')}
      >
        <IconRender
          icon="regular-bell"
          size={IconSizes.MD}
          color={CustomColorsRef.Secondary}
        />
      </button>

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(open => !open)}
          className="flex max-w-[14rem] items-center gap-2 rounded-xl py-1 pl-1 pr-1.5 text-left transition-colors hover:bg-gray-50 sm:pr-2"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E8EEF5] text-sm font-semibold text-imovSecondary">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              initial
            )}
          </span>

          {!compact ? (
            <span className="hidden min-w-0 flex-1 sm:block">
              <span className="block truncate text-sm font-bold leading-tight text-imovSecondary">
                {displayName}
              </span>
              {roleLabel ? (
                <span className="block truncate text-xs leading-tight text-gray-500">
                  {roleLabel}
                </span>
              ) : null}
            </span>
          ) : null}

          <IconRender
            icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
            size={IconSizes.SM}
            color={CustomColorsRef.Secondary}
          />
        </button>

        {isOpen ? (
          <div
            role="menu"
            className="absolute right-0 z-[60] mt-2 w-60 overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-lg"
          >
            {ACCOUNT_MENU_ITEMS.map(item => (
              <button
                key={item.kind === 'route' ? item.path : item.hash}
                type="button"
                role="menuitem"
                onClick={() => handleMenuItem(item)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-imovSecondary transition-colors hover:bg-imovPrimary/5 hover:text-imovPrimary focus-visible:bg-imovPrimary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-imovPrimary"
              >
                <IconRender
                  icon={item.icon}
                  size={IconSizes.SM}
                  color={CustomColorsRef.Secondary}
                />
                <span>{t(item.labelKey)}</span>
              </button>
            ))}

            <div className="my-1 border-t border-gray-100" />

            <div className="px-3 py-2">
              <LanguageSwitcher />
            </div>

            <div className="my-1 border-t border-gray-100" />

            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-imovSecondary transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-imovPrimary"
            >
              <IconRender
                icon="regular-right-from-bracket"
                size={IconSizes.SM}
                color={CustomColorsRef.Secondary}
              />
              <span>{t('common.logOut')}</span>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default UserMenu
